import { FC, lazy, PropsWithChildren, Suspense, useEffect, useState } from 'react';
import { HashRouter, Navigate, Outlet, Route, Routes } from 'react-router';
import { observer } from 'mobx-react-lite';
import { Stack, ThemeProvider } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import { IThemeSettings } from '@ai/common/src/types/themeSettings';
import { UserRole } from '@ai/common/src/types/user';
import { Auth } from '@ai/common/src/pages/auth/Auth';
import VerifyEmail from '@ai/common/src/pages/auth/VerifyEmail';
import SignUpForm from '@ai/common/src/pages/auth/SignUpForm';
import ResetPassword from '@ai/common/src/pages/auth/ResetPassword';
import { getUserStore } from '@ai/common/src/store/UserStore';
import { createTheme } from '@ai/common/src/theme';

import { LoginForm } from './pages/login/LoginForm';
import { MainMenuPanel } from './sections/main-menu-panel/MainMenuPanel';
import { SIDEBAR_STORAGE_KEY } from './constants/localStorageKeys';
import { paths } from './common/paths';

import './App.css';
import './i18n/config';
import logoImg from '/img/logo_color.svg';
import { sseStore } from '@ai/common/src/store/SseStore';
import { BrowserNotificationsProvider } from '@BrowserNotificationsProvider';

const ExpertListPage = lazy(() => import('./pages/ExpertListPage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const DialogHistory = lazy(() => import('./pages/DialogHistoryPage'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Users = lazy(() => import('./pages/Users'));
const SystemSettings = lazy(() => import('./pages/SystemSettings'));
const ActiveDialogs = lazy(() => import('./pages/ActiveDialogs'));

const initialSettings: IThemeSettings = {
  colorPreset: 'nlabteam',
  contrast: 'high',
  direction: 'ltr',
  layout: 'vertical',
  navColor: 'evident',
  paletteMode: 'light',
  responsiveFontSizes: true,
  stretch: false,
};

export const APP_WIDTH = {
  MENU_ICON: 56,
  MENU_TEXT: 250,
  MENU_PADDINGS: 16,
  SECTION: 1100,
  TAB: 1000,
  EXPERT_COMBOBOX: 200,
};

const settings = initialSettings;

const theme = createTheme({
  colorPreset: settings.colorPreset,
  contrast: settings.contrast,
  direction: settings.direction,
  paletteMode: 'light', //settings.paletteMode,
  responsiveFontSizes: settings.responsiveFontSizes,
});

export const App = observer(() => {
  const { currentUser } = getUserStore('platform');

  useEffect(() => {
    if (!sseStore.eventSource) {
      sseStore.init();
    }
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        style={{ maxWidth: 400 }}
      >
        <BrowserNotificationsProvider />
        <HashRouter>
          <Routes>
            <Route path={paths.index} element={<Navigate to={paths.home.index} replace />} />

            <Route
              path={paths.auth.index}
              element={<Auth logoImg={logoImg} frontendId={'platform'} />}
            >
              <Route path={paths.auth.login} element={<LoginForm />} />
              <Route
                path={paths.auth.signup}
                element={<SignUpForm pathLogin={paths.auth.login} frontendId="platform" />}
              />
              <Route
                path={paths.auth.verifyEmail}
                element={<VerifyEmail pathLogin={paths.auth.login} frontendId="platform" />}
              />
              <Route
                path={paths.auth.resetPassword}
                element={
                  <ResetPassword
                    pathLogin={paths.auth.login}
                    pathReset={paths.auth.resetPassword}
                    frontendId="platform"
                  />
                }
              />
            </Route>

            <Route element={<LayoutWithSidebar />}>
              <Route path={paths.expertList.index} element={<ExpertListPage />} />
              <Route path={paths.dashboard.index} element={<Dashboard />} />
              <Route path={paths.dialogHistory.index} element={<DialogHistory />} />
              <Route path={paths.activeDialogs.index} element={<ActiveDialogs />} />
              <Route path={paths.pricing.index} element={<Pricing />} />
              <Route path={paths.analytics.index} element={<Analytics />} />

              {currentUser?.role === UserRole.ADMIN && (
                <>
                  <Route path={paths.administration.users} element={<Users />} />
                  <Route path={paths.administration.settings} element={<SystemSettings />} />
                </>
              )}

              <Route path={'*'} element={<Navigate to={paths.home.index} replace />} />
            </Route>
          </Routes>
        </HashRouter>
      </SnackbarProvider>
    </ThemeProvider>
  );
});

export default App;

const LayoutWithSidebar = () => {
  const [isSideMenuPinned, setIsSideMenuPinned] = useState(
    localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true',
  );

  const setSideMenu = (value: boolean) => {
    setIsSideMenuPinned(value);
    localStorage.setItem(SIDEBAR_STORAGE_KEY, value.toString());
  };

  return (
    <Stack flexGrow={1} direction={'row'}>
      <MainMenuPanel isPinned={isSideMenuPinned} setIsPinned={setSideMenu} />

      <Stack flexGrow={1} sx={{ overflowX: 'hidden' }}>
        <ProtectedRoute>
          <Suspense>
            <Outlet />
          </Suspense>
        </ProtectedRoute>
      </Stack>
    </Stack>
  );
};

const ProtectedRoute: FC<PropsWithChildren> = ({ children }) => {
  const { currentUser } = getUserStore('platform');

  if (!currentUser) {
    return <Navigate to={paths.auth.login} state={{ from: location.hash }} replace />;
  }

  return children || '';
};
